import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportError, shouldIgnore } from '@/lib/errors/report';
import { logger } from '@/lib/logger';

import { fontStyles } from '@/styles/typography';

interface Props {
  children: ReactNode;
  /** Rendered inline in place of the children when they throw. */
  fallbackLabel: string;
  /** A caught error is cleared when this changes (e.g. a new quote). */
  resetKey?: unknown;
}

interface State {
  hasError: boolean;
}

/**
 * Scoped boundary for informational sections of the transaction module. A
 * render error inside collapses to an inline unavailable row instead of
 * bubbling to the module-wide ErrorBoundary, which toasts a submission
 * failure and resets the checkout step.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(error);
    if (shouldIgnore(error)) return;
    reportError(error, 'txn_module_matcha_error', errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className={fontStyles({ variant: 'BODY_MD', class: ['text-token-gray-800'] })}>
        {this.props.fallbackLabel}
      </div>
    );
  }
}
